import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageContainer } from "@/components/layout/PageContainer";
import { DetailView } from "@/components/layout/DetailView";
import { HeaderActions } from "./components/HeaderActions";
import { useItemDetail } from "../api/useItemDetail";
import { useSaveItem } from "../api/useSaveItem";
import { useCreateItem } from "../api/useCreateItem";
import { useDeleteItem } from "../api/useDeleteItem";
import { useDocSidebar } from "@/features/common/api/useDocSidebar";
import { InlineAssignments } from "@/features/common/components/InlineAssignments";
import { InlineShareActions } from "@/features/common/components/InlineShareActions";
import { DocChatPanel } from "@/features/common/components/DocChatPanel";
import { AttachmentListModal } from "@/features/common/components/AttachmentListModal";
import { TagListModal } from "@/features/common/components/TagListModal";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { useDetailPageChat } from "@/contexts/DetailPageChatContext";
import { erp } from "@/lib/erp";
import { getERPClient } from "@/lib/erp/client";
import { getQueryOptions } from "@/lib/config/queryConfig";
import type { DocSidebarData } from "@/lib/erp/docs";
import { isDirty } from "./utils/computeDirtyState";
import { useAuthStore } from "@/lib/store/authStore";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { cn } from "@/lib/utils";
import { useItemValidation } from "./hooks/useItemValidation";
import { useVariantCreation } from "./hooks/useVariantCreation";
import { useItemSave } from "./hooks/useItemSave";
import { resolveView } from "@/lib/views/registry";
import { SystemView } from "@/components/view/SystemView";
import { ExperienceView } from "@/components/view/ExperienceView";
import type { OrderContext } from "@/lib/views/types";
import { validateViewAgainstMeta } from "@/lib/views/utils/validateViewAgainstMeta";
import { routes } from "@/lib/config/routes";
import { useOptionalItemCreateMedia } from "./contexts/ItemCreateMediaContext";
import { useOptionalItemVariantDraft } from "./contexts/ItemVariantDraftContext";
import type { VariantDraft } from "@/lib/erp/variants";
import { createDefaultVariantDraft, variantDraftToItemAttributesRows } from "@/lib/erp/variants";
import { VARIANT_CAPABILITY_SCHEMA } from "@/lib/erp/variants";
import { generatePlannedVariantRows } from "./variants/utils";
import { useRegisterActiveDetailPage } from "@/hooks/useRegisterActiveDetailPage";
import { useTemplateVariantsSummary } from "./hooks/useTemplateVariantsSummary";
import { ChevronLeft } from "lucide-react";
import { HeaderMediaThumbnail } from "./components/HeaderMediaThumbnail";
import { BundleBadge } from "./components/BundleBadge";
import { ExogroSpecsCard } from "./components/ExogroSpecsCard";
import { useBundleConfig, customizationQueryKeys } from "@/features/customization/api/customizationAdminHooks";
import { EditBundleModal } from "./components/experience-views/item/widgets/EditBundleModal";
import { toast } from "sonner";
import { SectionNavRail, RAIL_FOOTPRINT } from "./components/SectionNavRail";
import { DetailPageTabBar } from "@/components/layout/DetailPageTabBar";
import { DetailPageSkeleton } from "@/features/doc/DetailPageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Reusable item detail content — works in both full-page route and slide-over panel.
 *
 * - variant="page"  → wraps with PageContainer + SectionNavRail (full-page route)
 * - variant="panel" → renders content directly, no page chrome (slide-over panel)
 */

export interface ItemDetailContentProps {
  /** Item name (item_code). Pass "" for new items. */
  itemName: string;
  /** Layout variant. Default: "page" */
  variant?: "page" | "panel";
  /** Called when panel back/close is clicked (panel mode only) */
  onClose?: () => void;
  /** Called when dirty state changes (panel mode — for dirty guard) */
  onDirtyChange?: (dirty: boolean) => void;
}

export function ItemDetailContent({
  itemName,
  variant = "page",
  onClose,
  onDirtyChange,
}: ItemDetailContentProps) {
  const isPanel = variant === "panel";
  const hideTabBar = false;
  const isNew = !itemName || itemName === "new";

  // Bundle detection — reuses same query as BundleComponentsSection (React Query deduplicates)
  const bundleConfig = useBundleConfig(itemName);

  // Chat panel state - use context to share with FloatingNav
  const { showChat, setShowChat, setOnChatClick } = useDetailPageChat();

  // Get active tab from context to detect tab changes
  const { activeTab } = useDetailPageTabs();
  const previousActiveTabRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Set chat click handler in context so FloatingNav can use it
  useEffect(() => {
    if (setOnChatClick) {
      setOnChatClick(() => () => setShowChat(!showChat));
      return () => {
        if (setOnChatClick) {
          setOnChatClick(undefined);
        }
      };
    }
  }, [setShowChat, setOnChatClick, showChat]);

  // Close chat when user switches tabs (navigates away from chat)
  useEffect(() => {
    if (showChat && activeTab && previousActiveTabRef.current !== null && previousActiveTabRef.current !== activeTab) {
      setShowChat(false);
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab, showChat]);

  // Prevent body scrolling when chat is open (page mode only — G3)
  useEffect(() => {
    if (isPanel) return; // Panel has its own scroll container
    if (showChat) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showChat, isPanel]);

  // Get auth state
  const authStore = useAuthStore();
  const { isAuthenticated } = authStore;

  // Get staged media from context (only available when isNew and wrapped with provider)
  const stagedMediaContext = useOptionalItemCreateMedia();

  // Track document version for child table resync
  const [docVersion, setDocVersion] = useState(0);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);

  // Fetch item detail (if not new)
  const { data: itemData, isLoading: isLoadingItem, error: itemError } = useItemDetail(
    isNew ? "" : itemName
  );

  // Fetch sidebar data (only for existing items)
  const sidebarQuery = useDocSidebar("Item", isNew ? undefined : itemName);

  // Fetch gallery images to get primary image for sidebar (Item-specific enhancement)
  const { data: galleryData } = useQuery({
    queryKey: ['item-gallery', itemName],
    queryFn: async () => {
      if (!itemName || isNew) return null;
      return await getERPClient().call<{
        item_code: string;
        media: Array<{
          name: string;
          url: string;
          alt: string;
          type: 'image' | 'video';
          isPrimaryImage: boolean;
          isPrimaryVideo: boolean;
          idx: number;
          thumbnailUrl: string | null;
        }>;
        primary_image: string | null;
        primary_video: string | null;
        schema_version: string;
        _contract?: string;
      }>('zaviago_backend.api.item_gallery.get_item_gallery', {
        item_code: itemName,
        contract: 'v2',
      });
    },
    enabled: !!itemName && !isNew,
    ...getQueryOptions('detail'),
  });

  // Find primary image and primary video from gallery (v2 contract)
  const { primaryImage, primaryVideo } = useMemo(() => {
    if (!galleryData?.media || !Array.isArray(galleryData.media)) {
      return { primaryImage: null, primaryVideo: null };
    }
    const sorted = [...galleryData.media].sort((a, b) => (a.idx || 0) - (b.idx || 0));
    const primaryImg = sorted.find((item) =>
      item.isPrimaryImage && item.type === 'image'
    ) ?? sorted.find((item) => item.type === 'image');
    const primaryVid = sorted.find((item) =>
      item.isPrimaryVideo && item.type === 'video'
    );
    return {
      primaryImage: primaryImg?.url || null,
      primaryVideo: primaryVid ? {
        url: primaryVid.url,
        alt_text: primaryVid.alt || '',
      } : null,
    };
  }, [galleryData?.media]);

  // Enhance sidebar data with primary image and video for Item doctype only
  const enhancedSidebarData = useMemo<DocSidebarData | undefined>(() => {
    const base = sidebarQuery.data as DocSidebarData | undefined;
    if (!base) return base;
    return {
      ...base,
      image: primaryImage || base.image,
      primaryVideo: primaryVideo,
    };
  }, [sidebarQuery.data, primaryImage, primaryVideo]);

  // Fetch meta schema - only when authenticated
  const { data: metaSchema, isLoading: isLoadingMeta } = useQuery({
    queryKey: ["meta", "Item"],
    queryFn: () => erp.meta.getDocTypeSchema("Item"),
    enabled: isAuthenticated,
  });

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Register this page for dev autofill
  useRegisterActiveDetailPage({
    doctype: "Item",
    metaSchema: metaSchema || undefined,
    formData,
    setFormData,
  });

  // Variant draft state (from context in create mode, local state otherwise)
  const variantDraftContext = useOptionalItemVariantDraft();
  const [variantDraftLocal, setVariantDraftLocal] = useState<VariantDraft>(() => createDefaultVariantDraft());

  // Use context if available (create mode), otherwise use local state (existing items)
  const variantDraft = variantDraftContext?.variantDraft ?? variantDraftLocal;
  const setVariantDraft = variantDraftContext?.setVariantDraft ?? setVariantDraftLocal;

  // Variant selection state (which variants to create)
  const [selectedVariantIdsLocal, setSelectedVariantIdsLocal] = useState<Set<string>>(() => {
    if (variantDraft.mode === 'item_attribute' && variantDraft.attributes.length > 0) {
      const capability = VARIANT_CAPABILITY_SCHEMA[variantDraft.mode];
      const allRows = generatePlannedVariantRows(variantDraft, { limit: capability.maxTotalVariants });
      return new Set(allRows.map(row => row.id));
    }
    return new Set();
  });

  const selectedVariantIds = variantDraftContext?.selectedVariantIds ?? selectedVariantIdsLocal;

  // Update selection when draft changes (reset to all selected) - only for local state
  useEffect(() => {
    if (!variantDraftContext && variantDraft.mode === 'item_attribute' && variantDraft.attributes.length > 0) {
      const capability = VARIANT_CAPABILITY_SCHEMA[variantDraft.mode];
      const allRows = generatePlannedVariantRows(variantDraft, { limit: capability.maxTotalVariants });
      setSelectedVariantIdsLocal(new Set(allRows.map(row => row.id)));
    } else if (!variantDraftContext) {
      setSelectedVariantIdsLocal(new Set());
    }
  }, [variantDraft, variantDraftContext]);

  // Derived: is template mode? (works for both create and edit mode)
  const isTemplateMode = useMemo(() => {
    return formData.has_variants === 1 || formData.has_variants === true;
  }, [formData.has_variants]);

  // Fetch existing variant attribute values for hydrating draft (edit mode only).
  // React Query deduplicates — same key is already fetched by ItemVariantsSummaryWidget.
  const isExistingTemplate = !isNew && isTemplateMode;
  const { data: variantSummary } = useTemplateVariantsSummary(
    itemName,
    isExistingTemplate,
  );

  // Refs to track hydration state and prevent snap-back from query refetches
  const hasHydratedRef = useRef(false);
  const hasInitializedDraftRef = useRef(false);
  const prevHasVariantsRef = useRef<boolean | null>(null);
  const currentDocRef = useRef<string>("");
  const isSavingRef = useRef(false);

  // Initialize variant draft from existing template attributes (edit mode only).
  // Populates attribute names from itemData and existing variant values from
  // variantSummary.attribute_options so users see current state and can add more.
  useEffect(() => {
    if (isNew || !itemData || !setVariantDraft) return;
    if (itemData.has_variants !== 1) return;
    if (hasInitializedDraftRef.current) return;

    // Guard: if the user already configured attributes in the draft (e.g. toggled
    // variants ON and added values before saving), don't overwrite their work.
    const draftHasPopulatedAttrs = variantDraft.attributes.length > 0 &&
      variantDraft.attributes.some(a => a.values.length > 0);
    if (draftHasPopulatedAttrs) {
      hasInitializedDraftRef.current = true;
      return;
    }

    const existingAttributes = (itemData as any).attributes;
    if (!Array.isArray(existingAttributes) || existingAttributes.length === 0) return;

    // Wait for variantSummary to arrive so we can populate existing values.
    // Without it, attributes show with empty values and user must re-add them.
    const attrOptions = variantSummary?.attribute_options;

    const draft: VariantDraft = {
      mode: 'item_attribute',
      attributes: existingAttributes
        .filter((row: any) => row.attribute)
        .map((row: any) => ({
          attribute: row.attribute,
          displayName: row.attribute,
          values: attrOptions?.[row.attribute] ?? [],
        })),
      useTemplateImage: false,
    };

    setVariantDraft(draft);
    // Only mark initialized once we have values (or if there are no existing variants)
    if (attrOptions || !variantSummary) {
      hasInitializedDraftRef.current = true;
    }
  }, [isNew, itemData, setVariantDraft, variantDraft, variantSummary]);

  // Initialize form data when item loads
  // GUARDED: Only hydrate on first load or document switch, never during save or when dirty
  useEffect(() => {
    if (currentDocRef.current !== itemName) {
      currentDocRef.current = itemName;
      hasHydratedRef.current = false;
      hasInitializedDraftRef.current = false;
    }
    if (itemData) {
      if (!hasHydratedRef.current) {
        setFormData(itemData);
        setOriginalData(itemData);
        setDocVersion(0);
        hasHydratedRef.current = true;
        return;
      }
      if (isSavingRef.current) return;
      const isDirtyNow = isDirty(originalData, formData);
      if (isDirtyNow) return;
      return;
    } else if (isNew && metaSchema) {
      const defaults: Record<string, any> = {};
      for (const field of metaSchema.fields) {
        if (field.default !== undefined) {
          defaults[field.fieldname] = field.default;
        }
        // Select fields with a single option and no explicit default:
        // auto-fill with the first (only) option — matches Frappe Desk behavior.
        // Fixes Create button staying disabled for fields like naming_series.
        else if (field.fieldtype === "Select" && field.options) {
          const opts = String(field.options).split("\n").filter(Boolean);
          if (opts.length === 1) {
            defaults[field.fieldname] = opts[0];
          }
        }
      }
      defaults.disabled = 0;
      setFormData(defaults);
      setOriginalData(defaults);
      setDocVersion(0);
    }
  }, [itemData, isNew, metaSchema, itemName]);

  // Sync variant draft to formData when draft changes (both create and edit mode)
  useEffect(() => {
    if (!isTemplateMode) return;
    setFormData((prev) => {
      const attributes = variantDraft.attributes.length > 0
        ? variantDraftToItemAttributesRows(variantDraft)
        : [];
      return {
        ...prev,
        has_variants: 1,
        variant_based_on: 'Item Attribute',
        attributes,
      };
    });
  }, [variantDraft, isTemplateMode]);

  // Reset variant draft when user toggles variants OFF (detects ON->OFF transition)
  useEffect(() => {
    const currentHasVariants = formData?.has_variants === 1 || formData?.has_variants === true;
    if (prevHasVariantsRef.current === true && !currentHasVariants) {
      setVariantDraft(createDefaultVariantDraft());
      hasInitializedDraftRef.current = false;
    }
    prevHasVariantsRef.current = currentHasVariants;
  }, [formData?.has_variants, setVariantDraft]);

  // Compute resetKey for child tables
  const resetKey = isNew ? `new-${docVersion}` : `${itemName}-${docVersion}`;

  // DEC-001: View resolution
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const devMode = usePreferencesStore((state) => state.devMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);

  const context: OrderContext | undefined = undefined;

  // Guard: Only allow system mode when in dev mode
  const systemMode = devMode ? viewMode === 'system' : false;

  // "Partner" (middleware_partner_key) is Exogro-only supplier price routing — the field
  // must not appear for other tenants (e.g. CSide). Gate on the Exogro tenant id embedded
  // in the company name (stable), rather than the mutable full company string.
  const tenantCompany = useAuthStore((s) => s.tenantCompany);
  const isPartnerKeyTenant = !!tenantCompany && tenantCompany.includes("574sn1poep");

  const resolvedView = useMemo(() => {
    if (!metaSchema) return null;
    const preferredStyle = !systemMode && experienceStyle !== 'auto'
      ? experienceStyle
      : undefined;
    const view = resolveView(
      metaSchema.name,
      context,
      systemMode,
      devMode,
      preferredStyle
    );
    // Hide the Partner field for non-Exogro tenants. Fields live in
    // formView.sections[].layout[].columns[].fieldname — shallow-clone only the
    // affected layout so the shared/cached view object is never mutated.
    if (view?.view?.formView?.sections && !isPartnerKeyTenant) {
      const sections = view.view.formView.sections.map((sec) => ({
        ...sec,
        layout: (sec.layout ?? [])
          .map((row) => ({
            ...row,
            columns: row.columns.filter((col) => col.fieldname !== "middleware_partner_key"),
          }))
          .filter((row) => row.columns.length > 0),
      }));
      return {
        ...view,
        view: { ...view.view, formView: { ...view.view.formView, sections } },
      };
    }
    return view;
  }, [metaSchema, context, systemMode, devMode, experienceStyle, isPartnerKeyTenant]);

  // Fieldname validation helper (dev-only)
  useEffect(() => {
    if (import.meta.env.DEV && resolvedView?.view && metaSchema) {
      validateViewAgainstMeta(metaSchema, resolvedView.view, {
        label: 'Item',
        showCoverage: true,
      });
    }
  }, [resolvedView, metaSchema]);

  // Mutations (MUST be called before any conditional returns to satisfy Rules of Hooks)
  const saveMutation = useSaveItem(isNew ? "" : itemName, originalData);
  const createMutation = useCreateItem({ navigateOnSuccess: false });
  const deleteMutation = useDeleteItem(isNew ? "" : itemName);

  // Compute dirty state
  const dirtyState = useMemo(() => {
    if (isNew) {
      return Object.values(formData).some((val) => val !== null && val !== undefined && val !== "");
    }
    return isDirty(originalData, formData);
  }, [formData, originalData, isNew]);

  // Required field check for Create button (new items only)
  const allRequiredFilled = useMemo(() => {
    if (!metaSchema || !isNew) return true;
    const LAYOUT_TYPES = new Set(["Table", "Table MultiSelect", "Section Break", "Column Break", "Tab Break", "HTML", "Fold"]);
    return metaSchema.fields
      .filter((f) => f.reqd === 1 && !LAYOUT_TYPES.has(f.fieldtype))
      .every((f) => {
        const v = formData[f.fieldname];
        return v !== null && v !== undefined && v !== "";
      });
  }, [formData, metaSchema, isNew]);

  // Clear a single field error (called when user clicks an errored row)
  const clearFieldError = useCallback((fieldname: string) => {
    setFieldErrors((prev) => {
      if (!prev[fieldname]) return prev;
      const next = { ...prev };
      delete next[fieldname];
      return next;
    });
  }, []);

  // Notify parent of dirty state changes (G14: skip initial mount value)
  const prevDirtyRef = useRef(dirtyState);
  useEffect(() => {
    if (prevDirtyRef.current !== dirtyState) {
      onDirtyChange?.(dirtyState);
      prevDirtyRef.current = dirtyState;
    }
  }, [dirtyState, onDirtyChange]);

  // Handle field change
  const handleFieldChange = (fieldname: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
  };

  // Extracted hooks for validation, variant creation, and save
  const validateForm = useItemValidation(metaSchema, formData);
  const maybeCreateVariantsAfterSave = useVariantCreation({
    skuOverridesByKey: variantDraftContext?.skuOverridesByKey,
  });
  const { handleSave, handleSaveWithPatch, handleModalSaveSuccess } = useItemSave({
    isNew,
    itemName,
    formData,
    setFormData,
    setOriginalData,
    setDocVersion,
    isSavingRef,
    metaSchema,
    saveMutation,
    createMutation,
    validateForm,
    maybeCreateVariantsAfterSave,
    stagedMediaContext,
    variantDraft,
    variantDraftContext,
    selectedVariantIds,
    setFieldErrors,
  });

  // Handle delete
  const handleDelete = () => {
    deleteMutation.mutate();
  };

  // ── Bundle convert/remove mutations (Plan 171, Epic 04) ──────────
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.currentTenant);
  const isVariant = !!formData?.variant_of;
  const [showBundleEditAfterConvert, setShowBundleEditAfterConvert] = useState(false);

  const convertToBundleMutation = useMutation({
    mutationFn: async () => {
      const client = getERPClient();
      // Check if a disabled Product Bundle already exists (Convert → Remove → Convert cycle)
      const existing = await client.call<Array<{ name: string; disabled: number }>>(
        "frappe.client.get_list",
        {
          doctype: "Product Bundle",
          filters: { new_item_code: itemName },
          fields: ["name", "disabled"],
          limit_page_length: 1,
        },
      );
      if (existing && existing.length > 0) {
        // Re-enable the existing disabled bundle
        await client.call("frappe.client.set_value", {
          doctype: "Product Bundle",
          name: existing[0].name,
          fieldname: "disabled",
          value: 0,
        });
      } else {
        // Create new Product Bundle
        await client.call("frappe.client.insert", {
          doc: { doctype: "Product Bundle", new_item_code: itemName, items: [] },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationQueryKeys.bundleConfig(tenant, itemName) });
      queryClient.invalidateQueries({ queryKey: ["Product Bundle", "batch-check"] });
      toast.success("Bundle created");
      setShowBundleEditAfterConvert(true);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create bundle");
    },
  });

  const removeBundleMutation = useMutation({
    mutationFn: async () => {
      const bundleName = bundleConfig.data?.bundle_name;
      if (!bundleName) throw new Error("Bundle not found");
      await getERPClient().call("frappe.client.set_value", {
        doctype: "Product Bundle",
        name: bundleName,
        fieldname: "disabled",
        value: 1,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationQueryKeys.bundleConfig(tenant, itemName) });
      queryClient.invalidateQueries({ queryKey: ["Product Bundle", "batch-check"] });
      toast.success("Bundle removed");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove bundle");
    },
  });

  // Loading state — structural skeleton (G11: conditional on variant)
  if (isLoadingItem || isLoadingMeta) {
    if (isPanel) {
      return <DetailPageSkeleton doctype="Item" />;
    }
    return (
      <PageContainer className="max-w-[860px]">
        <DetailPageSkeleton doctype="Item" />
      </PageContainer>
    );
  }

  // Error state (G11: conditional on variant)
  if (itemError && !isNew) {
    const errorContent = (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Error loading item</div>
      </div>
    );
    if (isPanel) return errorContent;
    return <PageContainer>{errorContent}</PageContainer>;
  }

  // Get page title
  const pageTitle = isNew
    ? "New Item"
    : itemData?.item_name || itemData?.item_code || "Item Detail";

  // Back navigation — conditional on variant (G2)
  const backNav = isPanel ? (
    <button
      type="button"
      onClick={onClose}
      className="inline-flex items-center gap-0.5 text-sm text-primary hover:text-primary/80 transition-colors -ml-1"
    >
      <ChevronLeft className="h-4 w-4" />
      <span>Back to list</span>
    </button>
  ) : (
    <Link
      to={routes.item.list}
      className="inline-flex items-center gap-0.5 text-sm text-primary hover:text-primary/80 transition-colors -ml-1"
    >
      <ChevronLeft className="h-4 w-4" />
      <span>Products</span>
    </Link>
  );

  const pageContent = (
    <div className={cn(
      "grid gap-4 sm:gap-6 grid-cols-1",
      showChat ? "items-stretch" : "items-start",
    )}>
      <div
        className={cn(
          showChat ? "relative min-h-0 min-w-0 overflow-hidden" : "relative min-w-0",
          showChat && !isPanel && "md:max-h-[calc(100vh-132px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:max-h-[calc(100vh-88px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]"
        )}
        style={showChat && !isPanel ? {
          height: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          maxHeight: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          overflow: 'hidden',
        } : undefined}
      >
        <DetailView
          scrollRef={scrollRef}
          contentClassName="px-2 md:px-4"
          title={pageTitle}
          media={
            !isNew ? (
              <HeaderMediaThumbnail
                image={primaryImage || enhancedSidebarData?.image || null}
                video={primaryVideo}
                alt={pageTitle}
              />
            ) : undefined
          }
          backNav={backNav}
          titleBadge={!isNew && bundleConfig.data?.is_bundle ? <BundleBadge /> : undefined}
          subtitle={undefined}
          actions={
            <HeaderActions
              onSave={handleSave}
              onPermanentDelete={!isNew ? handleDelete : undefined}
              isPermanentDeleting={deleteMutation.isPending}
              isSaving={saveMutation.isPending || createMutation.isPending}
              isDirty={dirtyState}
              isNew={isNew}
              itemName={!isNew ? itemName : undefined}
              attachmentCount={enhancedSidebarData?.attachments?.length ?? 0}
              onAttachments={!isNew ? () => setShowAttachmentDialog(true) : undefined}
              hideDetailDensity={false}
              allRequiredFilled={allRequiredFilled}
              isBundle={bundleConfig.data?.is_bundle ?? false}
              onConvertToBundle={!isNew && !isVariant && !bundleConfig.data?.is_bundle ? () => convertToBundleMutation.mutate() : undefined}
              isConvertingToBundle={convertToBundleMutation.isPending}
              onRemoveBundle={!isNew && bundleConfig.data?.is_bundle ? () => removeBundleMutation.mutate() : undefined}
              isRemovingBundle={removeBundleMutation.isPending}
            />
          }
          disableScroll={showChat}
          tabBar={<DetailPageTabBar />}
          hideTabBar={hideTabBar}
          belowTitle={
            !isNew && itemName ? (
              enhancedSidebarData ? (
                <div className="flex items-center gap-3">
                  <InlineAssignments
                    doctype="Item"
                    name={itemName}
                    assigned_to={enhancedSidebarData.assigned_to}
                    editable
                  />
                </div>
              ) : (
                <Skeleton className="h-9 w-9 rounded-full" />
              )
            ) : undefined
          }
          belowActions={
            !isNew && itemName ? (
              enhancedSidebarData ? (
                <InlineShareActions
                  doctype="Item"
                  name={itemName}
                  shares={enhancedSidebarData.shares}
                  public_share={enhancedSidebarData.public_share}
                  share_capabilities={enhancedSidebarData.share_capabilities}
                  liked_by={enhancedSidebarData.liked_by}
                  like_count={enhancedSidebarData.like_count}
                  comment_count={enhancedSidebarData.comment_count}
                  followed={enhancedSidebarData.is_followed}
                  tagCount={enhancedSidebarData.tags?.length ?? 0}
                  onTagClick={() => setShowTagModal(true)}
                  editable
                  onComment={() => setShowChat(!showChat)}
                />
              ) : (
                <Skeleton className="h-9 w-[340px] rounded-full" />
              )
            ) : undefined
          }
        >
          {showChat ? (
            !isNew && itemName ? (
              <div className="h-full w-full flex min-h-0">
                <DocChatPanel
                  doctype="Item"
                  name={itemName}
                  onClose={() => setShowChat(false)}
                />
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">
                Chat is only available after the item is created.
              </div>
            )
          ) : (
            <>
              {/* Exogro-only: manual product-spec editor (country/warranty/order-unit) */}
              {isPartnerKeyTenant && !isNew && itemName && (
                <ExogroSpecsCard
                  itemCode={itemName}
                  initial={{
                    country: formData?.middleware_country || "",
                    warranty: formData?.middleware_warranty || "",
                    orderUnit: formData?.middleware_order_unit || "",
                    locked: !!formData?.middleware_specs_locked,
                  }}
                />
              )}
              {metaSchema && resolvedView ? (
                resolvedView.mode === 'system' ? (
                  <SystemView
                    meta={metaSchema}
                    formData={formData}
                    onFieldChange={handleFieldChange}
                    disabled={saveMutation.isPending || createMutation.isPending}
                    onSave={handleSave}
                    onSubmit={handleSaveWithPatch}
                    isSaving={saveMutation.isPending || createMutation.isPending}
                    resetKey={resetKey}
                    documentName={isNew ? undefined : itemName}
                  />
                ) : resolvedView.view ? (
                  <ExperienceView
                    view={resolvedView.view}
                    meta={metaSchema}
                    formData={formData}
                    onFieldChange={handleFieldChange}
                    disabled={saveMutation.isPending || createMutation.isPending}
                    onSave={handleSave}
                    onSubmit={handleSaveWithPatch}
                    onSaveSuccess={handleModalSaveSuccess}
                    isSaving={saveMutation.isPending || createMutation.isPending}
                    resetKey={resetKey}
                    documentName={isNew ? undefined : itemName}
                    isNew={isNew}
                    fieldErrors={fieldErrors}
                    onClearFieldError={clearFieldError}
                  />
                ) : (
                  <div className="text-muted-foreground">No view available</div>
                )
              ) : (
                <div className="text-muted-foreground">Loading view configuration...</div>
              )}
            </>
          )}
        </DetailView>

        {/* Attachment list modal */}
        {!isNew && itemName && (
          <AttachmentListModal
            open={showAttachmentDialog}
            onOpenChange={setShowAttachmentDialog}
            doctype="Item"
            name={itemName}
            fallbackAttachments={enhancedSidebarData?.attachments}
          />
        )}

        {/* Tag list modal */}
        {!isNew && itemName && (
          <TagListModal
            open={showTagModal}
            onOpenChange={setShowTagModal}
            doctype="Item"
            name={itemName}
            tags={enhancedSidebarData?.tags ?? []}
          />
        )}

        {/* EditBundleModal after "Convert to bundle" (Plan 171, Epic 04) */}
        {showBundleEditAfterConvert && !isNew && itemName && (
          <EditBundleModal
            open={showBundleEditAfterConvert}
            onOpenChange={setShowBundleEditAfterConvert}
            bundleName={itemName}
            itemCode={itemName}
            initialItems={[]}
          />
        )}
      </div>
    </div>
  );

  // Panel mode: return content directly — no PageContainer, no SectionNavRail (G6)
  if (isPanel) {
    return pageContent;
  }

  // Page mode: wrap with PageContainer + SectionNavRail
  return (
    <>
      <div className="xl:pr-[var(--rail-footprint)]" style={{ "--rail-footprint": `${RAIL_FOOTPRINT}px` } as React.CSSProperties}>
        <PageContainer className="max-w-[860px]">
          {pageContent}
        </PageContainer>
      </div>
      <SectionNavRail />
    </>
  );
}
